import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RotateCcw } from "lucide-react";
import { useState } from "react";

import { ModalOverlay } from "@/components/ModalOverlay";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import type { ClearInventoryCacheResult, InventoryCacheSnapshot, Uuid } from "@tilt/admin-sdk";

/**
 * Operator surface for inspecting and selectively wiping the V0070
 * inventory snapshot cache for a single location. Drops in below
 * DepartmentsCard on LocationDetail's Overview tab.
 *
 * Renders one row per cached provider - each row carries its own
 * "Clear" button so an operator can wipe a single provider (e.g.
 * MarketCheck) without nuking parallel feeds. A header-level
 * "Clear all" button appears when 2+ providers are cached.
 *
 * Why per-provider matters: clearing a cache forces the next POS
 * inventory load to re-pull from the upstream provider, which costs
 * money. With multiple providers wired in (MarketCheck today, more
 * on deck), all-or-nothing is too blunt - you want to refresh the
 * one feed that went stale, not the ones that didn't.
 *
 * Permissions enforced server-side
 * (``caller.require_write_location``). Buttons render to anyone with
 * read access on the location; the API rejects unauthorized callers
 * with 403.
 */
export interface InventoryCachePanelProps {
  readonly locationId: Uuid;
  readonly locationName: string;
  readonly canWrite: boolean;
}

export function InventoryCachePanel({
  locationId,
  locationName,
  canWrite,
}: InventoryCachePanelProps): JSX.Element {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["inventory-cache", "list", locationId],
    queryFn: () => apiClient.listLocationInventoryCache(locationId),
    retry: false,
  });

  const [confirm, setConfirm] = useState<{ providerSlug: string | null } | null>(null);
  const [result, setResult] = useState<ClearInventoryCacheResult | null>(null);

  const onCleared = (r: ClearInventoryCacheResult) => {
    setResult(r);
    setConfirm(null);
    // The list query is now stale (one or all rows just got deleted).
    qc.invalidateQueries({ queryKey: ["inventory-cache", "list", locationId] });
  };

  const snapshots = list.data?.snapshots ?? [];

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight">Inventory cache</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Cached vehicle snapshots served to the POS inventory page. Clearing forces the next load
            to re-pull from the upstream provider - use when a feed went stale, a provider key
            changed, or after editing the binding.
          </p>
        </div>
        {canWrite && snapshots.length > 1 && (
          <button
            type="button"
            onClick={() => {
              setResult(null);
              setConfirm({ providerSlug: null });
            }}
            className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Clear all
          </button>
        )}
      </div>

      <div className="mt-3">
        {list.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading cached snapshots…
          </p>
        ) : list.isError ? (
          <p className="text-xs text-destructive">{describe(list.error)}</p>
        ) : snapshots.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No cached snapshots - the next POS inventory load will pull fresh from the upstream
            provider.
          </p>
        ) : (
          <ul className="divide-y rounded-md border bg-background">
            {snapshots.map((snap) => (
              <SnapshotRow
                key={snap.provider_slug}
                snap={snap}
                canWrite={canWrite}
                onClickClear={() => {
                  setResult(null);
                  setConfirm({ providerSlug: snap.provider_slug });
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {result && (
        <p className="mt-3 text-xs text-primary">
          {result.cleared > 0
            ? `Cleared ${result.cleared} provider snapshot${result.cleared === 1 ? "" : "s"}.`
            : "Nothing to clear."}
          {result.s3_errors.length > 0 && (
            <span className="ml-1 text-amber-600 dark:text-amber-400">
              {" "}
              S3 cleanup partial: {result.s3_errors.length} error
              {result.s3_errors.length === 1 ? "" : "s"} (DB rows still cleared).
            </span>
          )}
        </p>
      )}

      {confirm && (
        <ConfirmClearDialog
          locationId={locationId}
          locationName={locationName}
          providerSlug={confirm.providerSlug}
          onClose={() => setConfirm(null)}
          onSuccess={onCleared}
        />
      )}
    </div>
  );
}

function SnapshotRow(props: {
  snap: InventoryCacheSnapshot;
  canWrite: boolean;
  onClickClear: () => void;
}): JSX.Element {
  const { snap, canWrite, onClickClear } = props;
  const sizeKb = Math.max(1, Math.round(snap.bytes_size / 1024));
  const fetchedRel = snap.fetched_at ? relativeFromNow(snap.fetched_at) : "never";
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
      <div className="min-w-0">
        <p className="font-mono text-xs font-medium">{snap.provider_slug}</p>
        <p className="text-[11px] text-muted-foreground">
          {snap.item_count} item{snap.item_count === 1 ? "" : "s"} · refreshed {fetchedRel} · ~
          {sizeKb} KB
        </p>
      </div>
      {canWrite && (
        <button
          type="button"
          onClick={onClickClear}
          aria-label={`Clear ${snap.provider_slug} cache`}
          className="inline-flex items-center gap-2 rounded-md border bg-background px-2.5 py-1 text-[11px] font-medium hover:bg-muted"
        >
          <RotateCcw className="h-3 w-3" aria-hidden="true" /> Clear
        </button>
      )}
    </li>
  );
}

function ConfirmClearDialog(props: {
  locationId: Uuid;
  locationName: string;
  providerSlug: string | null;
  onClose: () => void;
  onSuccess: (r: ClearInventoryCacheResult) => void;
}): JSX.Element {
  const { locationId, locationName, providerSlug, onClose, onSuccess } = props;
  const [error, setError] = useState<string | null>(null);
  const clear = useMutation({
    mutationFn: () =>
      apiClient.clearLocationInventoryCache(
        locationId,
        providerSlug ? { providerSlug } : undefined,
      ),
    onSuccess,
    onError: (err: unknown) => setError(describe(err)),
  });

  const target = providerSlug ?? "all providers";
  return (
    <ModalOverlay ariaLabel="Confirm clear inventory cache" onClose={onClose}>
      <div className="w-full max-w-md rounded-lg bg-card p-5 shadow-lg">
        <h2 className="text-base font-semibold tracking-tight">
          Clear {providerSlug ? "this" : "all"} cached snapshot{providerSlug ? "" : "s"}?
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The next inventory load at <span className="font-medium">{locationName}</span> will
          re-fetch <span className="font-mono">{target}</span> from the upstream provider. This
          costs an upstream call - confirm only if the cached data is stale or broken.
        </p>
        {error && (
          <div className="mt-3 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={clear.isPending}
            className="rounded-md border bg-background px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              clear.mutate();
            }}
            disabled={clear.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {clear.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Clearing…
              </>
            ) : (
              "Clear cache"
            )}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

/** "5 min ago" / "2 h ago" - same shape the POS InventoryPage uses for
 *  its "Last refreshed" indicator. Local helper because tilt-admin
 *  doesn't have its own time-ago util yet; if we add a /lib/time, hoist
 *  this. */
function relativeFromNow(iso: string): string {
  const fetched = new Date(iso).getTime();
  if (Number.isNaN(fetched)) return "never";
  const diffMs = Date.now() - fetched;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
